import * as Sentry from '@sentry/vue'
import { sortBy } from 'lodash'
import PresentationApi from '@/api/presentation'
import AudienceApi from '@/api/audience'
import Socket from '@/socket'
import router from '@/router'
import Utils from '@/util'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import i18nPresenter from '@/i18nPresenter'

const SET_PRESENTATION_REPORT = 'SET_PRESENTATION_REPORT'
const SET_REPORT_ERROR = 'SET_REPORT_ERROR'
const SET_SELECTED_PRESENTATION = 'SET_SELECTED_PRESENTATION'
const SET_LOADING_REPORT = 'SET_LOADING_REPORT'
const SET_SHOWING_PANEL = 'SET_SHOWING_PANEL'
const SET_AUDIENCE_REPORT_SUMMARY = 'SET_AUDIENCE_REPORT_SUMMARY'
const SET_LOADING_AUDIENCE = 'SET_LOADING_AUDIENCE'
const SET_PREVIOUS_ROUTE = 'SET_PREVIOUS_ROUTE'
const SET_PARTICIPANT = 'SET_PARTICIPANT'
const SET_PARTICIPANT_ANSWERS = 'SET_PARTICIPANT_ANSWERS'
const SET_SHARED_REPORT_CURRENT_PRESENTATION_FLAG_INFO = 'SET_SHARED_REPORT_CURRENT_PRESENTATION_FLAG_INFO'

const _state = {
  selectedPresentation: null,
  presentationReport: null,
  isLoadingReport: true,
  isShowReportPanel: false,
  reportError: null,
  isLoadingAudience: false,
  audienceReportSummary: {},
  previousRouteName: null,
  participant: null,
  participantAnswers: null,
  sharedReportCurrentPresentationFlagInfo: null,
}

const _getters = {
  canUseDataMartIndividualReport: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-data-mart-individual-report')
  },
  canUseDataMartIndividualReportV2: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-data-mart-individual-report-v2')
  },
  canUseDataMartIndividualReportV5: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-data-mart-individual-report-v5')
  },
  isDemoData: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'switch-individual-report-demo')
  },
  canUseAIInsights: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-AHA-39375-ai-insights')
  },
  presentationReport(state) {
    return state.presentationReport
  },
  isLoadingReport(state) {
    return state.isLoadingReport
  },
  reportError(state) {
    return state.reportError
  },
  connectionError(state, rootState) {
    return rootState.socketId && rootState.socketDisconnect
  },
  isShowReportPanel(state) {
    return state.isShowReportPanel
  },
  reactionsSummary(state) {
    return (state.presentationReport && state.presentationReport.summary.reactions) || {}
  },
  engagementRate(state) {
    return state.presentationReport && state.presentationReport.summary.engagementRate
  },
  engagementLevel(state) {
    if (!state.presentationReport ||
      !state.presentationReport.summary) {
      return null
    }
    const { engagementRate } = state.presentationReport.summary
    if (!engagementRate) {
      return {
        title: 'No engagement yet!',
        icon: '',
      }
    }
    if (engagementRate >= 0.9 && engagementRate <= 1) {
      return {
        title: 'Amazing!',
        icon: '😲',
      }
    }
    if (engagementRate >= 0.7 && engagementRate < 0.9) {
      return {
        title: 'Wow, great job!',
        icon: '❤️',
      }
    }
    if (engagementRate >= 0.4 && engagementRate < 0.7) {
      return {
        title: 'Not bad!',
        icon: '👍',
      }
    }
    return {
      title: 'It\'s a good start!',
      icon: '😊',
    }
  },
  totalInteractions(state) {
    if (!state.presentationReport
        || !state.presentationReport.extended
        || !state.presentationReport.extended.slides
    ) {
      return 0
    }
    const totalResponses = state.presentationReport.extended.slides.reduce((accumulate, item) => {
      return accumulate + item.responseCount
    }, 0)

    const {
      numberOfLikes,
      numberOfHearts,
      numberOfLaughs,
      numberOfWows,
      numberOfSads,
    } = state.selectedPresentation

    const totalReactions =
      numberOfLikes + numberOfHearts + numberOfLaughs + numberOfWows + numberOfSads
    return totalResponses + totalReactions
  },
  audienceReport(state) {
    return state.presentationReport && state.presentationReport.audienceReport
  },

  mostDifficultQuestions(state) {
    if (!state.presentationReport ||
      !state.presentationReport.extended ||
      !state.presentationReport.extended.slides
    ) {
      return null
    }
    return state.presentationReport.extended.slides.filter((slide) => {
      return ['pickAnswer', 'multipleChoice', 'imageChoice'].includes(slide.type) &&
        slide.correctRate !== null &&
        slide.correctRate < 0.4
    }).sort((a, b) => {
      if (a.correctRate > b.correctRate) return 1
      if (a.correctRate < b.correctRate) return -1
      if (a.order > b.order) return 1
      if (a.order < b.order) return -1
      return 0
    })
  },
  avgCorrectRate(state) {
    if (!state.presentationReport ||
      !state.presentationReport.extended ||
      !state.presentationReport.extended.slides
    ) {
      return null
    }
    const slidesWithValidCorrectRates = state.presentationReport.extended.slides
      .filter((slide) => slide.correctRate !== null)

    const avgCorrectRates = slidesWithValidCorrectRates
      .reduce((totalRates, nextSlide) => {
        return totalRates + nextSlide.correctRate
      }, 0) / slidesWithValidCorrectRates.length

    return Math.round(avgCorrectRates * 100) || 0
  },
  mostEngagingSlides(state) {
    if (!state.presentationReport ||
      !state.presentationReport.extended ||
      !state.presentationReport.extended.slides
    ) {
      return null
    }
    const _sortByReactionsPlusResponses = (a, b) => {
      const { reactions: reactionsA, responseCount: responseCountA } = a
      const { reactions: reactionsB, responseCount: responseCountB } = b
      const reactionsCountA = Object.keys(reactionsA)
        .reduce((total, nextEmojiKey) => total + reactionsA[nextEmojiKey], 0) || 0
      const reactionsCountB = Object.keys(reactionsB)
        .reduce((total, nextEmojiKey) => total + reactionsB[nextEmojiKey], 0) || 0

      // Sort by Reactions + Responses count first
      if (reactionsCountA + responseCountA < reactionsCountB + responseCountB) {
        return 1
      }
      if (reactionsCountA + responseCountA > reactionsCountB + responseCountB) {
        return -1
      }

      // Sort by slide index second
      if (a.order > b.order) {
        return 1
      }
      if (a.order < b.order) {
        return -1
      }
      return 0
    }
    return state.presentationReport.extended.slides
      .sort(_sortByReactionsPlusResponses)
      .slice(0, 3)
  },
  selectedPresentation(state) {
    return state.selectedPresentation
  },

  privateFeedbacks(state) {
    return (state.presentationReport && state.presentationReport.privateFeedbacks) || []
  },
  newPrivateFeedbacks(state) {
    return (state.presentationReport && state.presentationReport.newPrivateFeedbacks) || {}
  },
  isPreviousRouteTeamFolderDetails(state) {
    return state.previousRouteName === 'TeamFolderDetail'
  },
  isPreviousShareRouteFolderDetails(state) {
    return state.previousRouteName === 'ShareFolderDetail'
  },
  isPreviousRouteTeam(state) {
    return state.previousRouteName === 'Team'
  },
  isPreviousRouteSharedWithMe(state) {
    return state.previousRouteName === 'SharedWithMe'
  },
  participant(state) {
    return state.participant
  },
  participantAnswers(state) {
    return state.participantAnswers
  },
}

const actions = {
  getPresentationFlagInfoBySharedToken: async ({ commit, state }, sharedToken) => {
    if (state.sharedReportCurrentPresentationFlagInfo
      && state.sharedReportCurrentPresentationFlagInfo.token === sharedToken) {
      return state.sharedReportCurrentPresentationFlagInfo.promise
    }

    const promise = PresentationApi.getPresentationFlagInfoBySharedToken(sharedToken)
    commit(SET_SHARED_REPORT_CURRENT_PRESENTATION_FLAG_INFO, { token: sharedToken, promise })

    return promise
  },
  getPresentationDetailByShareToken: async ({ rootGetters }, shareToken) => {
    const params = rootGetters['presentationsModule/canSharePublicOnly'] ? { publicOnly: true } : {}
    return PresentationApi.getPresentationDetailBySharedToken(shareToken, params)
  },
  getSharedReportTokenAction: async (_, presentationId) => {
    return PresentationApi.getSharedReportToken(presentationId)
  },
  resetSharedReportTokenAction: async (_, presentationId) => {
    return PresentationApi.resetSharedReportToken(presentationId)
  },
  getPresentationSharedReport: async ({ commit }, presentation) => {
    commit(SET_LOADING_REPORT, true)
    try {
      if (presentation && presentation.exportData && presentation.exportData.reportUrl) {
        const response = await fetch(presentation.exportData.reportUrl)
          .then((res) => res.json())
          .then((body) => ({ body }))
        const report = response.body || {}
        const feedbackV2 = await PresentationApi.getSharedReportPrivateFeedbacks(presentation.sharedToken)
        const newPrivateFeedbacks = feedbackV2 || {}
        commit(SET_PRESENTATION_REPORT, {
          ...report,
          newPrivateFeedbacks,
        })
      }
      commit(SET_LOADING_REPORT, false)
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_REPORT_ERROR, error)
      commit(SET_LOADING_REPORT, false)
    }
  },
  getPresentationReport: async ({ commit, state }, presentation) => {
    commit(SET_LOADING_REPORT, true)
    try {
      if (presentation && presentation.exportData && presentation.exportData.reportUrl) {
        const response = await fetch(presentation.exportData.reportUrl)
          .then((res) => res.json())
          .then((body) => ({ body }))
        const report = response.body || {}
        const presentationId = state.selectedPresentation && state.selectedPresentation.id
        const feedbackV2 = await PresentationApi.getPrivateFeedbacks(presentationId)
        const newPrivateFeedbacks = feedbackV2 || {}
        commit(SET_PRESENTATION_REPORT, {
          ...report,
          newPrivateFeedbacks,
        })
      }
      commit(SET_LOADING_REPORT, false)
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_REPORT_ERROR, error)
      commit(SET_LOADING_REPORT, false)
    }
  },
  getSharedReportAndMetricByPresentationId: async ({ commit }, params) => {
    try {
      commit(SET_LOADING_AUDIENCE, true)
      const { sharedToken, ...otherParam } = params
      otherParam.getReportWithScore = true
      const audienceReportSummary = await PresentationApi.getSharedReport(sharedToken, otherParam)
      commit(SET_AUDIENCE_REPORT_SUMMARY, audienceReportSummary)
      commit(SET_LOADING_AUDIENCE, false)
    } catch (e) {
      Sentry.captureException(e)
      commit(SET_LOADING_AUDIENCE, false)
      router.push({
        name: 'Presentations',
      })
    }
  },
  getReportAndMetricByPresentationId: async ({ commit }, params) => {
    try {
      commit(SET_LOADING_AUDIENCE, true)
      const { presentationId, ...otherParam } = params
      otherParam.getReportWithScore = true
      const audienceReportSummary = await PresentationApi.getReport(presentationId, otherParam)
      commit(SET_AUDIENCE_REPORT_SUMMARY, audienceReportSummary)
      commit(SET_LOADING_AUDIENCE, false)
    } catch (e) {
      Sentry.captureException(e)
      commit(SET_LOADING_AUDIENCE, false)
      router.push({
        name: 'Presentations',
      })
    }
  },
  getParticipant: async ({ commit }, params) => {
    const { presentationId, audienceId } = params
    const participant = await AudienceApi.getReportParticipantInfo(presentationId, audienceId)
    commit(SET_PARTICIPANT, participant)
  },
  // This is a duplicated version of getParticipantAnswers
  // It is used for shared report
  getSharedReportParticipantAnswers: async ({
    commit, dispatch,
  },
  params) => {
    commit(SET_PARTICIPANT_ANSWERS, [])
    const { sharedToken: presentationReportShareToken, audienceId } = params
    let participantAnswers = []

    const payload = {
      presentationReportShareToken,
      participantId: audienceId,
    }
    // Since in version 1 we doesn't support multiple session
    // So I comment out the condition check
    // if (rootGetters['multipleSessions/canUseMultipleSessions']) {
    //   participantAnswers = await AudienceApi.getParticipantReportV4(payload)
    // } else {
    const flagData = await dispatch('getPresentationFlagInfoBySharedToken', presentationReportShareToken)
    const canUseDataMartIndividualReportV5 = flagData?.['release-data-mart-individual-report-v5'] ?? false
    const canUseDataMartIndividualReportV2 = flagData?.['release-data-mart-individual-report-v2'] ?? false

    if (canUseDataMartIndividualReportV5) {
      participantAnswers = await AudienceApi.getSharedParticipantReportV5(payload)
    } else if (canUseDataMartIndividualReportV2) {
      participantAnswers = await AudienceApi.getSharedParticipantReportV2(payload)
    } else {
      participantAnswers = await AudienceApi.getSharedParticipantReport(payload)
    }
    commit(SET_PARTICIPANT_ANSWERS, sortBy(participantAnswers, ['slide_order']))
  },
  getParticipantAnswers: async ({ commit, getters, rootGetters }, params) => {
    commit(SET_PARTICIPANT_ANSWERS, [])
    const { presentationId, audienceId } = params
    let participantAnswers = []

    const payload = {
      presentationId,
      participantId: audienceId,
    }
    // Basically here we got a 2x2 matrix: multsession or not and v5 or v2.
    if (rootGetters['multipleSessions/canUseMultipleSessions']) {
      if (getters.canUseDataMartIndividualReportV5) {
        participantAnswers = await AudienceApi.getParticipantReportMultiSessionV5({
          participantId: audienceId,
          presentationId,
        })
      } else {
        participantAnswers = await AudienceApi.getParticipantReportMultiSessionV2({
          participantId: audienceId,
          presentationId,
        })
      }
    } else if (getters.canUseDataMartIndividualReportV5) {
      participantAnswers = await AudienceApi.getParticipantReportV5(payload)
    } else if (getters.canUseDataMartIndividualReportV2) {
      participantAnswers = await AudienceApi.getParticipantReportV2(payload)
    } else {
      // @deprecated, will be removed soon.
      participantAnswers = await AudienceApi.getParticipantReport(payload)
    }
    commit(SET_PARTICIPANT_ANSWERS, sortBy(participantAnswers, ['slide_order']))
  },
  generateSharedReportAction: async ({ commit, state }, { presentation, sharedToken }) => {
    try {
      if (presentation && presentation.id) {
        await PresentationApi.generateSharedReport(sharedToken)
        commit('updatePresentation', {
          exportData: {
            ...presentation.exportData,
            reportStatus: 'processing',
            reportUpdatedAt: new Date().getTime(),
          },

        }, { root: true })

        commit(SET_SELECTED_PRESENTATION, {
          ...state.selectedPresentation,
          exportData: {
            ...presentation.exportData,
            reportStatus: 'processing',
            reportUpdatedAt: new Date().getTime(),
          },
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_REPORT_ERROR, error)
    }
  },
  generateReportAction: async ({ commit, state }, presentation) => {
    try {
      if (presentation && presentation.id) {
        await PresentationApi.generateReport(presentation.id)
        commit('updatePresentation', {
          exportData: {
            ...presentation.exportData,
            reportStatus: 'processing',
            reportUpdatedAt: new Date().getTime(),
          },

        }, { root: true })

        commit(SET_SELECTED_PRESENTATION, {
          ...state.selectedPresentation,
          exportData: {
            ...presentation.exportData,
            reportStatus: 'processing',
            reportUpdatedAt: new Date().getTime(),
          },
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      commit(SET_REPORT_ERROR, error)
    }
  },

  selectPresentationAction: async (
    { commit, dispatch },
    { presentation },
  ) => {
    dispatch('clearPresentationReportAction')

    commit(SET_SELECTED_PRESENTATION, presentation)
    if (presentation && presentation.uniqueAccessCode) {
      dispatch('generateReportAction', presentation)
    }
  },

  selectSharedReportPresentationAction: async (
    { commit, dispatch },
    { presentation, sharedToken },
  ) => {
    dispatch('clearPresentationReportAction')

    commit(SET_SELECTED_PRESENTATION, presentation)
    if (presentation && presentation.uniqueAccessCode) {
      dispatch('generateSharedReportAction', { presentation, sharedToken })
    }
  },

  subscribeSharedReportAction: async ({ dispatch }, presentation) => {
    if (presentation && presentation.uniqueAccessCode && presentation.sharedToken) {
      Socket.getSocket().on('presentation:report_generated', (reportData) => {
        dispatch('getPresentationSharedReport', {
          exportData: reportData,
          sharedToken: presentation?.sharedToken,
        })
      })
    }
  },

  subscribeReportAction: async ({ dispatch }, presentation) => {
    if (presentation && presentation.uniqueAccessCode) {
      Socket.getSocket().on('presentation:report_generated', (reportData) => {
        dispatch('getPresentationReport', {
          exportData: reportData,
        })
      })
    }
  },

  unsubscribeReportAction: async ({ state }) => {
    if (state.selectedPresentation && state.selectedPresentation.uniqueAccessCode) {
      Socket.getSocket().emit('leave', `${state.selectedPresentation.uniqueAccessCode}-presenter`)
      Socket.getSocket().off('presentation:report_generated')
    }
  },

  openReportPanelAction: async ({ commit }) => {
    commit(SET_SHOWING_PANEL, true)
  },

  clearPresentationReportAction: async ({ commit }) => {
    commit(SET_PRESENTATION_REPORT, null)
    commit(SET_SELECTED_PRESENTATION, null)
    commit(SET_SHOWING_PANEL, false)
    commit(SET_LOADING_REPORT, true)
  },

  showPresentationReportAction: async ({ commit }, payload) => {
    const { presentation, trigger } = payload
    if (!presentation || !presentation.id) {
      return
    }
    const eventCategory = EVENT_CATEGORIES_BY_ROUTE_NAME[router.currentRoute?.name]
    Utils.trackGA4AndMixpanel(eventCategory, {
      eventAction: 'Presentation > Click view report',
      eventCategory,
      eventLabel: trigger,
      presentationId: presentation.id,
    })
    commit(SET_PREVIOUS_ROUTE, router.currentRoute?.name)
    router.push({
      name: 'PresentationReport',
      params: { id: presentation.id },
    })
  },
}

const mutations = {
  [SET_SHARED_REPORT_CURRENT_PRESENTATION_FLAG_INFO](state, payload) {
    state.sharedReportCurrentPresentationFlagInfo = payload
  },
  [SET_LOADING_REPORT](state, isLoadingReport) {
    state.isLoadingReport = isLoadingReport
  },
  [SET_PRESENTATION_REPORT](state, report) {
    state.presentationReport = report
    state.reportError = null
  },
  [SET_REPORT_ERROR](state, error) {
    state.reportError = error
  },
  [SET_SELECTED_PRESENTATION](state, presentation) {
    state.selectedPresentation = presentation
  },
  [SET_AUDIENCE_REPORT_SUMMARY](state, summary) {
    state.audienceReportSummary = summary
  },
  [SET_LOADING_AUDIENCE](state, isLoadingAudience) {
    state.isLoadingAudience = isLoadingAudience
  },
  [SET_SHOWING_PANEL](state, isShow) {
    state.isShowReportPanel = isShow
  },
  [SET_PREVIOUS_ROUTE](state, routeName) {
    state.previousRouteName = routeName
  },
  [SET_PARTICIPANT](state, participant) {
    state.participant = participant
  },
  [SET_PARTICIPANT_ANSWERS](state, participantAnswers) {
    state.participantAnswers = participantAnswers
  },
}

export const getRatingTooltip = (presentation) => {
  if (!presentation || !presentation.totalRatings) {
    return null
  }
  return `${i18nPresenter.t('Total ratings:')} <b>${presentation.totalRatings}</b>`
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
